/**
 * Pure math functions for SVG arc path generation.
 * Used by the donut chart controller.
 */

interface Point {
  x: number
  y: number
}

/**
 * Convert polar coordinates to Cartesian coordinates
 */
export function polarToCartesian(cx: number, cy: number, radius: number, angleInDegrees: number): Point {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180
  return {
    x: cx + radius * Math.cos(angleInRadians),
    y: cy + radius * Math.sin(angleInRadians),
  }
}

/**
 * Generate an SVG path string for an arc (donut segment)
 *
 * @param cx - Center X coordinate
 * @param cy - Center Y coordinate
 * @param innerRadius - Inner radius of the donut
 * @param outerRadius - Outer radius of the donut
 * @param startAngle - Start angle in degrees (0 = top)
 * @param endAngle - End angle in degrees
 * @returns SVG path string
 */
export function describeArc(
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  startAngle: number,
  endAngle: number
): string {
  // Handle full circle case
  const fullCircle = endAngle - startAngle >= 359.999
  if (fullCircle) {
    // For a full circle, we need to draw two arcs
    const midAngle = startAngle + 180
    const path1 = describeArc(cx, cy, innerRadius, outerRadius, startAngle, midAngle - 0.001)
    const path2 = describeArc(cx, cy, innerRadius, outerRadius, midAngle, endAngle - 0.001)
    return path1 + " " + path2
  }

  const outerStart = polarToCartesian(cx, cy, outerRadius, startAngle)
  const outerEnd = polarToCartesian(cx, cy, outerRadius, endAngle)
  const innerStart = polarToCartesian(cx, cy, innerRadius, startAngle)
  const innerEnd = polarToCartesian(cx, cy, innerRadius, endAngle)

  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1

  // Path: outer arc -> line to inner end -> inner arc (reverse) -> line to outer start
  return [
    "M",
    outerStart.x,
    outerStart.y,
    "A",
    outerRadius,
    outerRadius,
    0,
    largeArcFlag,
    1,
    outerEnd.x,
    outerEnd.y,
    "L",
    innerEnd.x,
    innerEnd.y,
    "A",
    innerRadius,
    innerRadius,
    0,
    largeArcFlag,
    0,
    innerStart.x,
    innerStart.y,
    "Z",
  ].join(" ")
}
